import {
  removeReferralRewardLines,
  syncReferralRewardBagPass,
} from "@/cart/bagPassFamily";
import { getMaticCount } from "@/cart/displayCart";
import {
  getFirstReferralCode,
  normalizeReferralCode,
} from "@/cart/referralCode";
import { lookUpReferralReward } from "@/cart/referralReward";
import type { CartStateGetSet } from "@/cart/types";
import GRAPHQL_CLIENT from "@/graphql/client";
import type { Cart } from "@/graphql/storefront/generated";
import { isMaticFamilyVariantId } from "@/utils/maticFamily";

type MaticReferralRewardError = Error & { cart?: Cart };

const createRewardError = (message: string, cart?: Cart) => {
  return Object.assign(new Error(message), {
    cart,
  }) as MaticReferralRewardError;
};

const getRewardErrorCart = (error: unknown) => {
  if (!(error instanceof Error) || !("cart" in error)) {
    return undefined;
  }

  return (error as MaticReferralRewardError).cart;
};

const getMaticLine = (cart?: Cart) => {
  return cart?.lines.edges.find(({ node }) =>
    isMaticFamilyVariantId(node.merchandise.id),
  );
};

export const getReferralCode = (
  get: CartStateGetSet["get"],
  cart?: Cart,
  codeOverride?: string,
) => {
  return (
    normalizeReferralCode(codeOverride) ||
    getFirstReferralCode(cart) ||
    get().referralCode
  );
};

const rollbackMaticLine = async (
  cartBeforeMaticChange: Cart | undefined,
  currentCart: Cart,
) => {
  const previousQuantity =
    getMaticLine(cartBeforeMaticChange)?.node.quantity ?? 0;
  const currentMaticLine = getMaticLine(currentCart);

  if (!currentMaticLine || currentMaticLine.node.quantity <= previousQuantity) {
    return currentCart;
  }

  if (previousQuantity === 0) {
    const { cartLinesRemove } = await GRAPHQL_CLIENT.RemoveFromCart({
      cartId: currentCart.id,
      lineIds: [currentMaticLine.node.id],
    });

    return cartLinesRemove?.cart as Cart | undefined;
  }

  const { cartLinesUpdate } = await GRAPHQL_CLIENT.UpdateCartLine({
    cartId: currentCart.id,
    lines: [{ id: currentMaticLine.node.id, quantity: previousQuantity }],
  });

  return cartLinesUpdate?.cart as Cart | undefined;
};

export const syncMaticReferralReward = ({ get, set }: CartStateGetSet) => {
  return async (cart: Cart, codeOverride?: string) => {
    const referralCode = getReferralCode(get, cart, codeOverride);
    const appliedReferralCode = getFirstReferralCode(cart);
    const maticCount = getMaticCount(cart);

    if (!referralCode) {
      set({ cartId: cart.id, cart });
      return cart;
    }

    if (maticCount === 0) {
      set({
        cartId: cart.id,
        cart,
        referralCode: appliedReferralCode || referralCode,
      });
      return cart;
    }

    const ensuredCart =
      appliedReferralCode === referralCode
        ? cart
        : ((await get().ensureReferralCode(referralCode)) as
            Cart | undefined) || get().cart;
    let nextCart = ensuredCart;

    if (!nextCart) {
      throw createRewardError(
        "syncMaticReferralReward: Applying referral code to cart failed",
        cart,
      );
    }

    const reward = await lookUpReferralReward(referralCode);

    // Without knowing the reward, any change could charge for a pass the code
    // covers or drop one it does; leave the lines as they are.
    if (reward === undefined) {
      set({ cartId: nextCart.id, cart: nextCart, referralCode });
      return nextCart;
    }

    try {
      nextCart = await syncReferralRewardBagPass(nextCart, reward);
    } catch (error) {
      throw createRewardError(
        "syncMaticReferralReward: Adding referral reward to cart failed",
        nextCart,
      );
    }

    set({
      cartId: nextCart.id,
      cart: nextCart,
      referralCode,
    });

    return nextCart;
  };
};

const recoverMaticCart = async (
  cartBeforeMaticChange: Parameters<typeof rollbackMaticLine>[0],
  currentCart: Parameters<typeof rollbackMaticLine>[1],
  syncReferralReward: ReturnType<typeof syncMaticReferralReward>,
  codeOverride?: string,
) => {
  const previousQuantity =
    getMaticLine(cartBeforeMaticChange)?.node.quantity ?? 0;
  const cartWithoutBagPassReward = await removeReferralRewardLines(currentCart);
  const rolledBackCart = cartWithoutBagPassReward
    ? await rollbackMaticLine(cartBeforeMaticChange, cartWithoutBagPassReward)
    : await rollbackMaticLine(cartBeforeMaticChange, currentCart);

  if (!rolledBackCart || previousQuantity === 0) {
    return rolledBackCart;
  }

  return syncReferralReward(rolledBackCart, codeOverride);
};

export const syncReferralRewardWithRollback = ({
  get,
  set,
}: CartStateGetSet) => {
  const syncReferralReward = syncMaticReferralReward({ get, set });

  return async (
    cartBeforeMaticChange: Cart | undefined,
    mutatedCart: Cart,
    codeOverride?: string,
  ) => {
    try {
      return await syncReferralReward(mutatedCart, codeOverride);
    } catch (error) {
      try {
        const rewardErrorCart = getRewardErrorCart(error) || mutatedCart;
        const recoveredCart = await recoverMaticCart(
          cartBeforeMaticChange,
          rewardErrorCart,
          syncReferralReward,
          codeOverride,
        );

        if (recoveredCart) {
          set({ cartId: recoveredCart.id, cart: recoveredCart });
        }
      } catch (rollbackError) {
        console.error(
          "syncReferralRewardWithRollback: Failed to roll back cart after referral reward error",
          rollbackError,
        );
      }

      throw error;
    }
  };
};
